import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { useOrganization } from '@/hooks/useOrganization';
import { assumirAtendimento, ClienteJaAtribuidoError } from '@/services/clientes.service';

interface AutoAssumirInput {
  clienteId: string;
  profileId: string;
  profileNome: string;
}

/**
 * Atribui o cliente ao atendente quando este lhe responde, desde que o cliente
 * ainda não tenha responsável. Usado pelo ChatInput em fire-and-forget: a
 * mensagem já foi enviada com sucesso, por isso erros aqui não devem incomodar
 * o utilizador. Se outro atendente ganhou a corrida (ClienteJaAtribuidoError),
 * ignoramos em silêncio — é resultado esperado, não erro.
 */
export function useAutoAssumirAoResponder() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ clienteId, profileId, profileNome }: AutoAssumirInput) =>
      assumirAtendimento({ clienteId, profileId, profileNome, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['clientes-reais'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      toast({
        title: t('panel.takenOverTitle'),
        description: t('panel.takenOverDesc'),
      });
    },
    onError: (error) => {
      if (error instanceof ClienteJaAtribuidoError) return; // corrida perdida — esperado
      captureError(error, { extra: { hook: 'useAutoAssumirAoResponder' } });
    },
  });
}
